// Shared by the instrument page's charts (InstrumentCharts,
// InstrumentChartsPerformance). Their data is one value per day, aligned with
// the page's list of dates, and the x axis is the day's index. A category axis
// with thousands of date labels, animations, a dot per day and dozens of
// annotation elements per chart made the page take seconds to draw.

const afterFitWidth70 = (axis) => { axis.width = 70; };

// [{ x: day index, y: value }]: the format Chart.js reads without parsing
export const toPoints = (values) => values.map((y, x) => ({ x, y }));

// The signals in NOTIFICATION as day indices: buys and sells (1, -1), RE-BUYs
// (2), and each buy with its sell as a pair of indices
export function buildSignalMarkers(notification) {
  const lines = [];
  const rebuys = [];
  const spans = [];
  let buy = null;
  notification.forEach((n, i) => {
    if (n === 2) {
      rebuys.push(i);
    } else if (n === 1 || n === -1) {
      lines.push(i);
      if (n === 1) {
        buy = i;
      } else if (buy !== null) {
        spans.push(buy, i);
        buy = null;
      }
    }
  });
  return { lines, rebuys, spans };
}

// Draws the signals of options.plugins.signalMarkers: a red line per buy and
// sell, a blue one per RE-BUY, and the time from a buy to its sell in green
export const signalMarkersPlugin = {
  id: "signalMarkers",
  afterDatasetsDraw(chart, args, options) {
    const { lines = [], rebuys = [], spans = [] } = options;
    const { ctx, chartArea, scales } = chart;
    const { left, right, top, bottom } = chartArea;
    const x = scales.x;

    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, right - left, bottom - top);
    ctx.clip();

    ctx.fillStyle = "rgba(0, 255, 0, 0.2)";
    for (let i = 0; i + 1 < spans.length; i += 2) {
      const from = x.getPixelForValue(spans[i]);
      ctx.fillRect(from, top, x.getPixelForValue(spans[i + 1]) - from, bottom - top);
    }

    ctx.lineWidth = 2;
    const drawLines = (indices, color) => {
      ctx.strokeStyle = color;
      ctx.beginPath();
      indices.forEach((index) => {
        const px = x.getPixelForValue(index);
        ctx.moveTo(px, top);
        ctx.lineTo(px, bottom);
      });
      ctx.stroke();
    };
    drawLines(lines, "rgba(255, 0, 0, 0.5)");
    drawLines(rebuys, "rgba(0, 0, 255, 0.8)");

    ctx.restore();
  },
};

// The options of one chart. range: [first, last] day index shown; with
// showDates the x axis is labelled with the dates.
export const chartOptions = ({ title, range, dates, showDates = false, signals, y = {}, annotations }) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  parsing: false,
  normalized: true,
  layout: { padding: { left: 0, right: 0, top: 10, bottom: 10 } },
  scales: {
    x: {
      type: "linear",
      min: range[0],
      max: range[1],
      // bar charts default to a half-day offset, which would misalign them
      offset: false,
      grid: { display: true, offset: false },
      // No label at the ends: one there would stick out and make this chart
      // narrower than the others
      ticks: showDates
        ? {
            display: true, maxRotation: 45, minRotation: 45,
            precision: 0, includeBounds: false, maxTicksLimit: 30,
            callback: (value) => dates[value] ?? "",
          }
        : { display: false },
    },
    y: {
      type: "linear",
      position: "left",
      display: true,
      ticks: { padding: 5, align: "end", crossAlign: "near" },
      grid: { display: true },
      afterFit: afterFitWidth70,
      title: { display: true, text: title },
      ...y,
    },
  },
  plugins: {
    legend: { display: false },
    title: { display: false },
    signalMarkers: signals,
    ...(annotations && { annotation: { annotations } }),
  },
});
